// InvQuoteList.js - Invoice/Quote List (web, adapted from React Native)
import React, { useState, useEffect } from 'react';
import { Button, Table, Modal, Typography, Space, message, Tabs, Tag, Alert } from 'antd';
import InvQuote from './InvQuote';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { baseUrl_SPB } from '../../BaseUrl';
import { useAuth } from '../../AuthProvider';

const { Title, Text } = Typography;

const initialInvoices = [];
const initialQuotes = [];

const InvQuoteList = ({ onSelect, showOnlyInvoices }) => {
  const [invoices, setInvoices] = useState(initialInvoices);
  const [quotes, setQuotes] = useState(initialQuotes);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('invoice');
  const [selectedItem, setSelectedItem] = useState(null);
  const auth = useAuth();

  // --- NEW STATES FOR MULTI-SELECT RECONCILIATION ENGINE ---
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Generate invoice/quote number with prefix
  const generateNumber = (type) => {
    const prefix = type === 'quote' ? 'Q' : 'I';
    const uniqueId = uuidv4(); 
    return `${prefix}-${uniqueId}`;
  };

  // Load data from backend
  const loadData = async (type) => {
    if (!auth?.token) return;
    try {
      const response = await axios.get(`${baseUrl_SPB}/api/v1/invorquote/getinvorquote?type=${type}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`,
        }
      });
      if (type === 'invoice') {
        setInvoices(response.data);
      } else {
        setQuotes(response.data);
      }
    } catch (error) {
      message.error('Failed to load ' + type + 's');
      console.error(error);
    }
  };

  useEffect(() => {
    loadData('invoice');
    loadData('quote');
    // eslint-disable-next-line
  }, [auth?.token]);

  const openModal = (type, item = null) => {
    setModalType(type);
    if (!item) {
      setSelectedItem({ invoiceOrQuoteNumber: generateNumber(type) });
    } else {
      let patchedItem = { ...item };
      if (item.customerName && item.customerId) {
        patchedItem.customerObj = { label: item.customerName, value: item.customerId };
      }
      if (item.id) {
        patchedItem.id = item.id;
      }
      setSelectedItem(patchedItem);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  const handleSave = async (item, type) => {
    const payload = {
      ...item,
      id: item.id || null,
      invoiceOrQuoteNumber: item.invoiceOrQuoteNumber,
      customerId: item.customerId,
      customerEmail: item.customerEmail || '',
      customerName: item.customerName,
      customerPhone: item.customerPhone || '',
      dueDate: item.dueDate,
      dated: item.dated,
      taxOption: item.taxOption,
      invOrQuoteItems: item.items || item.invOrQuoteItems || [],
      invPaymentItems: item.payments || item.invPaymentItems || [],
      grandTotal: item.total,
      totalGST: item.totalGST,
      type: type,
      paymentRecords: item.payments || item.invPaymentItems || [],
    };
    try {
      await axios.post(
        `${baseUrl_SPB}/api/v1/invorquote/save`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`,
          },
        }
      );
      loadData('invoice');
      loadData('quote');
      closeModal();
      message.success(`${type === 'invoice' ? 'Invoice' : 'Quote'} saved!`);
    } catch (error) {
      message.error('Failed to save ' + (type === 'invoice' ? 'invoice' : 'quote'));
      console.error(error);
    }
  };

  const handleCopy = (record) => {
    const { id, invoiceOrQuoteNumber, ...rest } = record;
    const type = record.type || 'invoice';
    const copied = {
      ...rest,
      invoiceOrQuoteNumber: generateNumber(type),
    };
    openModal(type, copied);
  };

  const handleConvertToInvoice = (record) => {
    const { id, invoiceOrQuoteNumber, ...rest } = record;
    const copied = {
      ...rest,
      invoiceOrQuoteNumber: generateNumber('invoice'),
      type: 'invoice',
    };
    openModal('invoice', copied);
  };

  // Helper utility to calculate total paid, remaining balance, and status text
  const getPaymentStatus = (record) => {
    if (record.type === 'quote') {
      return { statusText: 'Quote', color: 'default', totalPaid: 0, balance: 0 };
    }

    const total = parseFloat(record.grandTotal || record.total || 0);
    const payments = record.invPaymentItems || record.paymentRecords || [];
    
    const totalPaid = payments.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    const balance = total - totalPaid;

    if ( balance <= 0 || record.paid === 'Y'){
      return { statusText: 'Fully Paid', color: 'success', totalPaid, balance: 0 };
    } else if (totalPaid > 0) {
      return { statusText: 'Partially Paid', color: 'warning', totalPaid, balance };
    } else {
      return { statusText: 'Unpaid', color: 'error', totalPaid: 0, balance: total };
    }
  };

  // Calculate cumulative aggregate calculation values dynamically
  const totalSelectedSum = selectedRows.reduce((acc, current) => {
    return acc + parseFloat(current.grandTotal || current.total || 0);
  }, 0);

  const columns = [
    { title: 'Number', dataIndex: 'invoiceOrQuoteNumber', key: 'invoiceOrQuoteNumber' },
    { title: 'Customer', key: 'customer', render: (_, record) => record.customerName || record.customer || '' },
    { title: 'Dated', dataIndex: 'dated', key: 'dated' },
    { title: 'Due Date', dataIndex: 'dueDate', key: 'dueDate' },
    { title: 'Total', key: 'total', render: (_, record) => `$${parseFloat(record.grandTotal || record.total || 0).toFixed(2)}` },
    
    // --- NEW DYNAMIC STATUS COLUMN ---
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      render: (_, record) => {
        const { statusText, color, balance } = getPaymentStatus(record);
        return (
          <Space direction="vertical" size={0}>
            <Tag color={color}>{statusText}</Tag>
            {balance > 0 && (
              <span style={{ fontSize: '11px', color: '#8c8c8c' }}>
                Bal: ${balance.toFixed(2)}
              </span>
            )}
          </Space>
        );
      }
    },

    {
      title: onSelect ? 'Select' : 'Action',
      key: 'action',
      render: (_, record) => {
        const isQuote = record.type === 'quote';
        return (
          onSelect
            ? (
              <Button 
                type="primary" 
                size="small" 
                onClick={() => {
                  // Direct bypass mapping logic array wraps a singular object item natively
                  onSelect([record]);
                }}
              >
                Select Only This
              </Button>
            ) : (
              <Space>
                <Button onClick={() => openModal(record.type, record)}>View</Button>
                <Button onClick={() => handleCopy(record)}>Create a Copy</Button>
                {isQuote && (
                  <Button type="dashed" onClick={() => handleConvertToInvoice(record)}>
                    Convert to Invoice
                  </Button>
                )}
              </Space>
            )
        );
      },
    },
  ];

  // Sort invoices by date (latest first)
  const sortedInvoices = [...invoices].sort((a, b) => {
    const dateA = new Date(a.dated || a.dueDate);
    const dateB = new Date(b.dated || b.dueDate);
    return dateB - dateA;
  });

  // Dynamic distribution into tabs via helper calculation
  const paidInvoices = sortedInvoices.filter(inv => getPaymentStatus(inv).statusText === 'Fully Paid');
  const unpaidInvoices = sortedInvoices.filter(inv => getPaymentStatus(inv).statusText !== 'Fully Paid');

  // --- NEW ANTD ROW SELECTION IMPLEMENTATION SETUP ---
  const rowSelection = onSelect ? {
    selectedRowKeys,
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    }
  } : undefined;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
      {!onSelect && <>
        <Title level={2}>Invoices & Quotes</Title>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={() => openModal('invoice')}>Create Invoice</Button>
          <Button onClick={() => openModal('quote')}>Create Quote</Button>
        </Space>
      </>}

      {/* NEW FLOATING ACTION ALERT: Renders multi-select execution panel options */}
      {onSelect && selectedRowKeys.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text>
                Selected <strong>{selectedRowKeys.length}</strong> invoice(s) — Total Combined: <strong>${totalSelectedSum.toFixed(2)}</strong>
              </Text>
              <Button 
                type="primary" 
                size="small" 
                onClick={() => {
                  onSelect(selectedRows);
                  setSelectedRowKeys([]);
                  setSelectedRows([]);
                }}
              >
                Reconcile Selection Together
              </Button>
            </div>
          }
        />
      )}

      <Tabs defaultActiveKey="unpaid" style={{ marginBottom: 16 }}>
        <Tabs.TabPane tab="Outstanding / Partial" key="unpaid">
          <Table
            rowSelection={rowSelection}
            dataSource={unpaidInvoices}
            columns={columns}
            pagination={{ pageSize: 8 }}
            rowKey="id"
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Paid Invoices" key="paid">
          <Table
            rowSelection={rowSelection}
            dataSource={paidInvoices}
            columns={columns}
            pagination={{ pageSize: 8 }}
            rowKey="id"
          />
        </Tabs.TabPane>
      </Tabs>
      {!showOnlyInvoices && <>
        <Title level={4} style={{ marginTop: 32 }}>Quotes</Title>
        <Table dataSource={quotes} columns={columns} pagination={{ pageSize: 8 }} rowKey="id" />
      </>}
      <Modal
        open={showModal}
        onCancel={closeModal}
        footer={null}
        width={800}
        destroyOnClose
      >
        <InvQuote
          type={modalType}
          item={selectedItem}
          onSave={handleSave}
          onCancel={closeModal}
        />
      </Modal>
    </div>
  );
};

export default InvQuoteList;